import { css, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import { Text } from '@atlaskit/primitives-next';
import { token } from '@atlaskit/tokens';

import {
	SURVEY_CHART_HEIGHT,
	surveyChartTicks,
	surveyChoices,
} from '../../jsm-portal-playground-hr-data';
import { jsmPortalPlaygroundHrMessages as messages } from '../../jsm-portal-playground-hr-messages';
import { EASE_OUT, staggerDelay } from '../jsm-playground-motion';

const tickOffset = (index: number) => `${(index / (surveyChartTicks.length - 1)) * 100}%`;

export const SurveyChoiceChart = () => {
	const intl = useIntl();
	const data = surveyChoices
		.map((choice) =>
			intl.formatMessage(messages.chartDatum, {
				choice: intl.formatMessage(choice.label),
				count: choice.responses,
			}),
		)
		.join(', ');

	return (
		<div
			css={chartStyles}
			role="img"
			aria-label={intl.formatMessage(messages.chartLabel, { data })}
		>
			<div css={plotRowStyles}>
				<div css={yAxisStyles}>
					{surveyChartTicks.map((tick, index) => (
						<span key={tick} css={yAxisLabelStyles} style={{ top: tickOffset(index) }}>
							<Text size="small" color="color.text.subtle">
								{intl.formatNumber(tick)}
							</Text>
						</span>
					))}
				</div>
				<div css={plotStyles}>
					{surveyChartTicks.map((tick, index) => (
						<span
							key={tick}
							css={[index === surveyChartTicks.length - 1 ? baselineStyles : gridlineStyles]}
							style={{ top: tickOffset(index) }}
						/>
					))}
					<div css={barsStyles}>
						{surveyChoices.map((choice, index) => (
							<div key={choice.id} css={barSlotStyles}>
								<span
									css={barStyles}
									style={{
										height: `${(choice.barHeight / SURVEY_CHART_HEIGHT) * 100}%`,
										animationDelay: staggerDelay(index, 70),
									}}
								/>
							</div>
						))}
					</div>
				</div>
			</div>
			<div css={xAxisStyles}>
				{surveyChoices.map((choice) => (
					<div key={choice.id} css={xAxisSlotStyles}>
						<span css={xAxisTickStyles} />
						<Text size="small" color="color.text.subtle">
							{intl.formatMessage(choice.label)}
						</Text>
					</div>
				))}
			</div>
		</div>
	);
};

const Y_AXIS_GUTTER = '45px';

const grow = keyframes({
	from: { transform: 'scaleY(0)' },
	to: { transform: 'scaleY(1)' },
});

const chartStyles = css({
	display: 'flex',
	flexDirection: 'column',
	paddingBlockStart: token('space.300'),
	width: '100%',
});

const plotRowStyles = css({
	display: 'flex',
	width: '100%',
	height: `${SURVEY_CHART_HEIGHT}px`,
});

const yAxisStyles = css({
	position: 'relative',
	flexShrink: 0,
	width: Y_AXIS_GUTTER,
});

const yAxisLabelStyles = css({
	position: 'absolute',
	insetInlineEnd: token('space.100'),
	transform: 'translateY(-50%)',
	whiteSpace: 'nowrap',
});

const plotStyles = css({
	position: 'relative',
	flexGrow: 1,
	minWidth: 0,
});

const gridlineStyles = css({
	position: 'absolute',
	insetInline: 0,
	borderBlockStart: `${token('border.width', '1px')} solid ${token('color.border')}`,
});

// The 0 line is the axis, not a gridline, so it is heavier and darker than the four above it.
const baselineStyles = css({
	position: 'absolute',
	insetInline: 0,
	borderBlockStart: `${token('border.width.selected', '2px')} solid ${token('color.border.bold')}`,
});

const barsStyles = css({
	position: 'relative',
	display: 'flex',
	alignItems: 'flex-end',
	width: '100%',
	height: '100%',
});

const barSlotStyles = css({
	display: 'flex',
	alignItems: 'flex-end',
	justifyContent: 'center',
	flexBasis: 0,
	flexGrow: 1,
	minWidth: 0,
	height: '100%',
});

// Grows by transform rather than height, so the entrance never triggers layout.
const barStyles = css({
	display: 'block',
	width: '30%',
	maxWidth: '61px',
	borderStartStartRadius: token('radius.xsmall'),
	borderStartEndRadius: token('radius.xsmall'),
	backgroundColor: token('color.chart.categorical.1'),
	transformOrigin: 'bottom',
	animation: `${grow} 700ms ${EASE_OUT} both`,
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});

const xAxisStyles = css({
	display: 'flex',
	boxSizing: 'border-box',
	width: '100%',
	paddingInlineStart: Y_AXIS_GUTTER,
});

const xAxisSlotStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	flexBasis: 0,
	flexGrow: 1,
	gap: token('space.050'),
	minWidth: 0,
	paddingBlockEnd: token('space.100'),
	textAlign: 'center',
});

const xAxisTickStyles = css({
	display: 'block',
	height: '6px',
	borderInlineStart: `${token('border.width', '1px')} solid ${token('color.border')}`,
});
